import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { BlueprintChat, type BlueprintQuestion } from "@/components/blueprint/BlueprintChat";
import { useCreateGoal, useGoals, useSaveGoalProfile } from "@/lib/queries";
import { TIER_2_OPENER, splitList, useBlueprintSave } from "@/lib/blueprint";

/**
 * 7b — the authority blueprint.
 *
 * Fills `authority_profiles`, the goal-scoped table that already exists for
 * this track: what she is worth listening to about, whether she has a
 * platform, how much stage time she has done, and where she wants to show up.
 *
 * Same five-question shape as the ICP, and the same reason for using a
 * conversation: "topics of expertise" as a form field collects three nouns,
 * while the same question asked out loud gets the sentence she would open a
 * talk with.
 *
 * **Saved against the `auth` goal, not the org.** Authority answers belong to
 * a track — a founder who later pivots to funding has not stopped being worth
 * listening to, and her answers should still be there when she comes back to
 * this one. That is what the goal-scoped tables are for.
 */
export const Route = createFileRoute("/authority")({
  validateSearch: z.object({ tier: z.literal(2).optional() }),
  component: Authority,
});

/**
 * Three questions, not five.
 *
 * The conversation is the gate on the feed now, so its length is the price of
 * seeing the rooms — and a five-question price on a screen she reached by
 * tapping "unlock" is where she closes the tab. Three she finishes.
 *
 * The three that stayed are the three the ranking reads. What went: `speakingExperience` and `bookTitle` — how much stage time she has done and what she has already published. Both describe her past rather than what an outlet is matched on, and neither is read when the map is composed.
 */
const TIER_1: BlueprintQuestion[] = [
  {
    field: "topicsOfExpertise",
    ask: "What are you worth listening to about? Not your product — the thing you know that most people in the room do not.",
    hint: "Worth talking through. This is what a stage has to be about.",
    placeholder: "How reconciliation actually breaks at scale…",
  },
  {
    field: "preferredChannels",
    ask: "Where would you actually want to show up? Guest podcasts, writing, speaking, community, building in public.",
    hint: "Name the ones you would say yes to this month.",
    placeholder: "Guest podcasts and writing…",
  },
  {
    field: "reachMetrics",
    ask: "Last one. Where do you already have an audience, and roughly how big? A rough range is fine.",
    hint: "“about 4k on LinkedIn, 600 on the newsletter”",
    placeholder: "About 4k on LinkedIn…",
  },
];

/**
 * Tier two — the answers that decide *which* stage, not whether she deserves
 * one.
 *
 * Everything here lands in `extra`, the goal profile's open column. That is
 * deliberate rather than lazy: none of it has a typed column yet, and adding
 * five before knowing which ones the ranking actually reads would be four
 * migrations to undo. When one earns a filter it gets promoted.
 */
const TIER_2: BlueprintQuestion[] = [
  {
    field: "signatureArgument",
    ask: "What do you believe about your field that most people in it would argue with?",
    hint: "The disagreement is the talk. A room that already agrees has nothing to invite you for.",
    placeholder: "That reconciliation is a data problem, not a headcount one…",
  },
  {
    field: "dreamStages",
    ask: "Which stages, podcasts or publications would genuinely mean something to you?",
    hint: "Real names. This is the answer that points at specific rooms.",
    placeholder: "Fintech Brainfood, Money20/20…",
  },
  {
    field: "provenSubject",
    ask: "What have you already said in public that landed — a post, a talk, an answer that people repeated?",
    hint: "What worked once is the shortest route to what works next.",
    placeholder: "A post about why our first integration failed…",
  },
  {
    field: "audienceWanted",
    ask: "Who do you want in the audience? Not how many — who.",
    hint: "Authority is only worth what the room can do for you.",
    placeholder: "Heads of ops at brokerages…",
  },
  {
    field: "nextYearInvitation",
    ask: "Last one. What do you want to be invited to by this time next year?",
    hint: "Naming it is what lets Omina spot the rooms two steps before it.",
    placeholder: "A main-stage slot at…",
  },
];

function Authority() {
  const { tier } = Route.useSearch();
  const isDeep = tier === 2;

  const goals = useGoals();
  const createGoal = useCreateGoal();
  const saveGoalProfile = useSaveGoalProfile();
  const navigate = useNavigate();

  /**
   * `useBlueprintSave` finds this track's goal row, or creates one.
   *
   * Normally it exists: choosing this path on `/path` writes
   * `org_settings.active_goal` and `ensureGoal` makes the row in the same
   * request. But these screens are reachable by their own URLs, and a founder
   * arriving that way had a finished blueprint refused — five answers lost to
   * a row that could simply have been created.
   */
  const save = useBlueprintSave({
    type: "auth",
    goals,
    createGoal,
    saveGoalProfile,
    /**
     * The feed, not the five.
     *
     * She has just spent a conversation describing exactly who she sells to, and
     * `/matches` answers that with three rooms and two behind a lock — the screen
     * whose job is to prove the product works to someone who has not paid, and
     * whose next control is a button back to the radar to scan again. Landing
     * there after the questions reads as being sent to the queue after doing the
     * work.
     *
     * The feed is what the answers actually bought: every room that ranks, in the
     * order the blueprint just changed.
     */
    onDone: () => void navigate({ to: "/feed" }),
  });

  return (
    <BlueprintChat
      title={isDeep ? "Sharpening your matches" : "Your authority blueprint"}
      opener={
        isDeep
          ? TIER_2_OPENER
          : "A few questions to unlock better matches, based on what you actually need. Answer them out loud or type — either is fine."
      }
      questions={isDeep ? TIER_2 : TIER_1}
      saving={save.saving}
      error={save.error}
      doneTitle="That is the blueprint."
      doneNote={
        isDeep
          ? "Omina now knows the argument, the stages and the audience. The next scan is ranked against all of it."
          : "It sets what Omina looks for on this track: rooms where saying that out loud compounds."
      }
      saveLabel={isDeep ? "Save and rescan" : "Save and see my rooms"}
      /* The feed either way — it is where both tiers are entered from now.
         Tier one used to return to `/matches`, which was true when the nudge
         lived there and is a detour now. */
      exitTo="/feed"
      onSave={(a) =>
        isDeep
          ? save.run({
              extra: {
                signatureArgument: a.signatureArgument,
                dreamStages: splitList(a.dreamStages),
                provenSubject: a.provenSubject,
                audienceWanted: a.audienceWanted,
                nextYearInvitation: a.nextYearInvitation,
              },
            })
          : save.run({
              ...(splitList(a.topicsOfExpertise)
                ? { topicsOfExpertise: splitList(a.topicsOfExpertise) }
                : {}),
              ...(a.speakingExperience ? { speakingExperience: a.speakingExperience } : {}),
              ...(splitList(a.preferredChannels)
                ? { preferredChannels: splitList(a.preferredChannels) }
                : {}),
              // Free text, deliberately not parsed into a number. "About 4k on
              // LinkedIn" is the honest answer; turning it into 4000 would
              // invent a precision she did not give.
              ...(a.reachMetrics ? { reachMetrics: { stated: a.reachMetrics } } : {}),
              ...(a.bookTitle ? { bookTitle: a.bookTitle } : {}),
            })
      }
    />
  );
}
